import React from 'react';
import { css } from '@emotion/react';
import { useSelector } from 'react-redux';
import { Button, Checkbox, FormControl, InputLabel, Menu, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useTranslation } from 'react-i18next';
import { color } from '@/styles';
import { IGetUnitRes } from '@/types/api/unit/getUnit';
import { IGetResidentRes } from '@/types/api/resident/getResident';
import { NOTIFICATION_ICON_PATH } from '@/constants/notification';
import { selfDataSelector, showMedicalContentSelector } from '@/redux/config/selfData';

export interface ResidentFilters {
  categoryCondition: CategoryCondition;
  unitId: number | null;
  residentId: number | null;
  notificationTypes: string[];
}

export type CategoryCondition = 'all' | 'deviceAssigned' | 'deviceAttached' | 'unit';
interface CategoryOption {
  value: string;
  label: string;
  condition: CategoryCondition;
  unitId: number | null;
}

interface Props {
  title: string;
  units: IGetUnitRes[];
  residents: IGetResidentRes[];
  filters: ResidentFilters;
  onUpdate: () => void;
  onChangeFilters: (filters: ResidentFilters) => void;
}

const Container = css`
  position: sticky;
  z-index: 2;
  top: 0;
  padding: 16px 0;
  background-color: var(--surface-list-header);
`;

const HeaderContainer = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const TitleContainer = css`
  display: flex;
  align-items: center;
`;

const Title = css`
  font-size: 20px;
  font-weight: bold;
  margin-right: 16px;
`;

const UpdateButton = css`
  border-radius: 100px;
  color: ${color.dark_gray};
  background-color: var(--button-bg);
  &:hover {
    background-color: var(--button-bg);
  }
`;

const RightElementContainer = css`
  display: flex;
`;

const FilterButton = css`
  display: flex;
  align-items: center;
  column-gap: 4px;
`;

const FilterPanel = css`
  padding: 16px;
  background: ${color.white};
`;

const SelectorContainer = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 12px;
`;

const CheckBoxContainer = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CheckBoxItem = css`
  display: inline-flex;
  align-items: center;
  min-height: 32px;
`;

const CheckBoxLabel = css`
  display: inline-flex;
  align-items: center;
  column-gap: 8px;
  color: ${color.dark_gray};
`;

const NotificationIcon = css`
  width: 24px;
  height: 24px;
`;

export const NotificationResidentListHeader = React.memo(
  ({ title, units, residents, filters, onUpdate, onChangeFilters }: Props) => {
    const { t } = useTranslation();
    const { selfUser } = useSelector(selfDataSelector);
    const showMedicalContent = useSelector(showMedicalContentSelector);
    const [anchorElFilter, setAnchorElFilter] = React.useState<null | HTMLElement>(null);
    const residentLabel = showMedicalContent ? t('resident_medical', '利用者') : t('resident', '入居者');

    const notificationTypeOptions = React.useMemo(
      () => [
        { value: 'bigBladder', label: t('almostNotificiation', 'そろそろ通知'), showIcon: true },
        { value: 'urinated', label: t('checkNotificiation', 'でたかも通知'), showIcon: true },
        { value: 'gotUp', label: t('gotUpNotificiation', '起き上がり'), showIcon: true },
        { value: 'resetUrine', label: t('measurementRestartNotification', '測定再開通知'), showIcon: false },
        { value: 'dfreeDeviceRemoved', label: t('detachedSensorNotification', '外れ通知'), showIcon: false },
        { value: 'upsideDown', label: t('sensorUpsideDownNotification', '上下逆通知'), showIcon: false },
        { value: 'batteryDead', label: t('lowBatteryNotification', '電池切れ通知'), showIcon: false },
        { value: 'batteryAlert', label: t('deviceAlertNotification', '異常通知'), showIcon: false },
      ],
      [t],
    );

    const categoryOptions = React.useMemo<CategoryOption[]>(
      () => [
        { value: '', label: t('all', 'すべて'), condition: 'all', unitId: null },
        {
          value: 'deviceAssigned',
          label: t('DFreeIsAssigned', 'デバイス登録中'),
          condition: 'deviceAssigned',
          unitId: selfUser.nursingHomeUnitId ?? null,
        },
        {
          value: 'deviceAttached',
          label: t('DFreeIsAttached', '測定中'),
          condition: 'deviceAttached',
          unitId: selfUser.nursingHomeUnitId ?? null,
        },
        ...(!selfUser.hasUnitPerm
          ? units.map((unit) => ({
              value: `unit-${unit.id}`,
              label: unit.name,
              condition: 'unit' as const,
              unitId: unit.id,
            }))
          : []),
      ],
      [selfUser.hasUnitPerm, selfUser.nursingHomeUnitId, t, units],
    );

    const selectedCategory = React.useMemo(() => {
      const selected = categoryOptions.find((option) => {
        if (option.condition !== filters.categoryCondition) return false;
        if (option.condition !== 'unit') return true;
        return option.unitId === filters.unitId;
      });
      return selected?.value ?? '';
    }, [categoryOptions, filters.categoryCondition, filters.unitId]);

    const sortedResidents = React.useMemo(() => {
      return [...residents].sort((a, b) => {
        const nameA = `${a.lastName ?? ''}${a.name ?? ''}`;
        const nameB = `${b.lastName ?? ''}${b.name ?? ''}`;
        return nameA.localeCompare(nameB, 'ja');
      });
    }, [residents]);

    const handleChangeCategory = (event: SelectChangeEvent<string>) => {
      const selected = categoryOptions.find((option) => option.value === event.target.value);
      if (!selected) return;
      onChangeFilters({
        categoryCondition: selected.condition,
        unitId: selected.unitId,
        residentId: null,
        notificationTypes: filters.notificationTypes,
      });
    };

    const handleChangeResident = (event: SelectChangeEvent<string>) => {
      onChangeFilters({
        categoryCondition: filters.categoryCondition,
        unitId: filters.unitId,
        residentId: event.target.value === '' ? null : +event.target.value,
        notificationTypes: filters.notificationTypes,
      });
    };

    const handleToggleNotificationType = (notificationType: string) => () => {
      const notificationTypes = filters.notificationTypes.includes(notificationType)
        ? filters.notificationTypes.filter((type) => type !== notificationType)
        : [...filters.notificationTypes, notificationType];

      onChangeFilters({
        categoryCondition: filters.categoryCondition,
        unitId: filters.unitId,
        residentId: filters.residentId,
        notificationTypes,
      });
    };

    const handleFilterClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorElFilter(event.currentTarget);
    };

    const handleCloseFilter = () => {
      setAnchorElFilter(null);
    };

    const isFilterActive =
      (!selfUser.hasUnitPerm && filters.unitId !== null) ||
      filters.residentId !== null ||
      filters.notificationTypes.length > 0;

    return (
      <div css={Container}>
        <div css={HeaderContainer}>
          <div css={TitleContainer}>
            <div css={Title}>{title}</div>
            <Button css={UpdateButton} onClick={onUpdate} startIcon={<RefreshIcon color='primary' />}>
              {t('update', '更新')}
            </Button>
          </div>
          <div css={RightElementContainer}>
            <Button onClick={handleFilterClick} css={FilterButton}>
              <span style={{ color: color.black }}>{t('filter', '絞り込み')}</span>
              <>{isFilterActive ? <FilterAltIcon /> : <ArrowDropDownIcon />}</>
            </Button>
          </div>
        </div>
        <Menu
          open={!!anchorElFilter}
          anchorEl={anchorElFilter}
          onClose={handleCloseFilter}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ sx: { width: 360, maxWidth: 'calc(100vw - 32px)' } }}
        >
          <div css={FilterPanel}>
            <div css={SelectorContainer}>
              <FormControl fullWidth size='small'>
                <InputLabel id='notification-category-filter-label'>{t('category', 'カテゴリー')}</InputLabel>
                <Select
                  labelId='notification-category-filter-label'
                  value={selectedCategory}
                  label={t('category', 'カテゴリー')}
                  onChange={handleChangeCategory}
                >
                  {categoryOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth size='small'>
                <InputLabel id='notification-resident-filter-label'>{residentLabel}</InputLabel>
                <Select
                  labelId='notification-resident-filter-label'
                  value={filters.residentId ? String(filters.residentId) : ''}
                  label={residentLabel}
                  onChange={handleChangeResident}
                >
                  <MenuItem value=''>{t('notSelected', '未選択')}</MenuItem>
                  {sortedResidents.map((resident) => (
                    <MenuItem key={resident.id} value={String(resident.id)}>
                      {resident.lastName} {resident.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div css={CheckBoxContainer}>
              {notificationTypeOptions.map((option) => (
                <label key={option.value} css={CheckBoxItem}>
                  <Checkbox
                    color='primary'
                    checked={filters.notificationTypes.includes(option.value)}
                    onChange={handleToggleNotificationType(option.value)}
                  />
                  <span css={CheckBoxLabel}>
                    {option.showIcon && (
                      <img
                        css={NotificationIcon}
                        src={NOTIFICATION_ICON_PATH[option.value] ?? NOTIFICATION_ICON_PATH.default}
                        alt={option.label}
                      />
                    )}
                    {option.label}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </Menu>
      </div>
    );
  },
);
